import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { TextField, Grid, Box, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useCategory from "hooks/useCategory";

const MAX_IMAGE_SIZE = 150 * 1024 * 1024; // 150MB
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];

const CategoryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get("id");
  const { createcategory, getcategoryById, updatecategory, loading } = useCategory();

  const [formData, setFormData] = useState({
    name: "",
    image: null,
    imagePreview: null,
  });

  const [imageError, setImageError] = useState("");

  const validateImage = (file) => {
    if (!file) return false;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setImageError("Only JPG, JPEG, PNG, and PDF formats are allowed.");
      return false;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("File size should be under 5MB.");
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!validateImage(file)) {
      setFormData((prev) => ({
        ...prev,
        image: null,
        imagePreview: null,
      }));
      return;
    }

    setImageError(""); // Clear previous errors if valid
    setFormData((prev) => ({
      ...prev,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Name is required and cannot be empty.");
      return;
    }

    if (!formData.image) {
      alert("Image/PDF is required.");
      return;
    }

    const formDataToSubmit = new FormData();
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("image", formData.image);

    if (categoryId) {
      const updateResponse = await updatecategory(categoryId, formDataToSubmit);
      if (updateResponse?.success) {
        navigate("/category-management");
      } else {
        alert(updateResponse?.message);
      }
      return;
    }

    const createResponse = await createcategory(formDataToSubmit);
    if (createResponse?.success) {
      setFormData({
        name: "",
        image: null,
        imagePreview: null,
      });
      navigate("/category-management");
    } else {
      alert(createResponse?.message);
    }
  };

  const fetchCategory = async () => {
    const response = await getcategoryById(categoryId);
    if (response?.success && response.data.image) {
      setFormData({
        name: response.data.name,
        image: response.data.image,
        imagePreview: response.data.image,
      });
    }
  };

  useEffect(() => {
    if (categoryId) {
      fetchCategory();
    }
  }, [categoryId]);

  return (
    <Box
      sx={{
        width: "100%",
        background: "white",
        minHeight: `calc(100vh - 120px)`,
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <MDTypography variant="h5" component="h1" align="center" gutterBottom>
        Category Form
      </MDTypography>
      <MDTypography variant="caption" color="text" align="center" paragraph>
        Fill out the details below to register/update Category.
      </MDTypography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Name*
          </MDTypography>
          <TextField
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </Grid>

        {/* Image Upload */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="body2" gutterBottom>
            Image / PDF* <span style={{ fontSize: "10px" }}>(JPG, JPEG, PNG, PDF - Max 5MB)</span>
          </MDTypography>
          <input
            accept="image/*,application/pdf" // Allow images and PDF
            type="file"
            name="image"
            onChange={handleImageChange}
            style={{ display: "none" }}
            id="file-upload"
          />
          <label htmlFor="file-upload">
            <MDButton
              fullWidth
              variant="contained"
              color="info"
              component="span"
              sx={{
                padding: "8px 20px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#0056b3",
                },
              }}
            >
              Upload File
            </MDButton>
          </label>
          {imageError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {imageError}
            </Typography>
          )}
        </Grid>

        {/* Image Preview */}
        <Grid item xs={12}>
          {formData.imagePreview && (
            <Box
              sx={{
                mt: 2,
                width: "100%",
                height: "200px",
                border: "2px dashed #ccc",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {formData.image?.type === "application/pdf" ||
              formData.imagePreview?.includes(".pdf") ? (
                <iframe
                  src={formData.imagePreview}
                  title="PDF Preview"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={formData.imagePreview}
                  alt="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              )}
              <MDButton
                size="small"
                variant="contained"
                color="error"
                sx={{ position: "absolute", top: 8, right: 8 }}
                onClick={() =>
                  setFormData((prev) => ({ ...prev, image: null, imagePreview: null }))
                }
              >
                Remove
              </MDButton>
            </Box>
          )}
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <MDBox>
            <MDButton
              type="submit"
              variant="contained"
              color="dark"
              fullWidth
              onClick={handleSubmit}
              sx={{
                marginTop: "3rem",
                padding: "10px 20px",
                borderRadius: "12px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              {loading ? "Submitting..." : categoryId ? "Update Category" : "Submit"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CategoryForm;
